// Practice Review — per-question review of one past Daily Focus set / Weekly
// Mock: stem, options (correct highlighted, wrong pick flagged), explanation.

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { examRunwayAPI, ReviewResponse } from '../services/examRunwayAPI';
import MarkdownRenderer from '../components/MicroLearning/MarkdownRenderer';
import { useTheme } from '../contexts/ThemeContext';

const PracticeReview: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const C = {
    border: dark ? '#262c35' : '#e6e8ec',
    muted: dark ? '#aab1ba' : '#64748b',
    text: dark ? '#e6e8eb' : '#0f172a',
    bg: dark ? '#0f1216' : '#f8fafc',
    card: dark ? '#171b21' : '#fff',
    inset: dark ? '#12161c' : '#fff',
    backArrow: dark ? '#aab1ba' : '#475569',
    correct: dark ? '#86c232' : '#16a34a',
    correctBg: dark ? 'rgba(134,194,50,0.12)' : '#f0fdf4',
    wrong: dark ? '#ff6b6b' : '#dc2626',
    wrongBg: dark ? 'rgba(255,107,107,0.15)' : '#fef2f2',
  };
  const isTg = location.pathname.startsWith('/tg/');
  const { sessionId } = useParams<{ sessionId: string }>();
  const [data, setData] = useState<ReviewResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    examRunwayAPI
      .sessionDetail(sessionId)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || 'Could not load this session.'));
  }, [sessionId]);

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 16px',
          background: C.card,
          borderBottom: `1px solid ${C.border}`,
          position: 'sticky',
          top: 0,
        }}
      >
        <button
          onClick={() => navigate(isTg ? '/tg/practice-history' : '/practice-history')}
          aria-label="Back"
          style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer', color: C.backArrow }}
        >
          ←
        </button>
        <h1 style={{ margin: 0, fontSize: '1.05rem', color: C.text }}>Review</h1>
      </header>

      <main style={{ maxWidth: 720, margin: '0 auto', padding: '12px 16px 80px' }}>
        {err && <div style={{ color: C.wrong, padding: 16 }}>{err}</div>}
        {!data && !err && <div style={{ color: C.muted, padding: 16 }}>Loading…</div>}
        {data && (
          <>
            <div
              data-testid="practice-review-summary"
              style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 12 }}
            >
              <div style={{ fontSize: 13, color: C.muted }}>
                {data.type === 'weekly_mock' ? 'Weekly Mock' : 'Daily Focus'} · {data.subject} · {data.period}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginTop: 4 }}>
                {data.correct} / {data.total} <span style={{ color: C.muted, fontSize: 16 }}>({data.score_pct}%)</span>
              </div>
            </div>

            {data.questions.map((q, i) => (
              <div
                key={q.question_id}
                style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 10 }}
              >
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>
                  Q{i + 1}
                  {q.topic ? ` · ${q.topic}` : ''} ·{' '}
                  <span style={{ color: q.is_correct ? C.correct : C.wrong, fontWeight: 700 }}>
                    {q.is_correct ? 'Correct' : 'Wrong'}
                  </span>
                </div>
                <div style={{ fontSize: 15, color: C.text }}>
                  <MarkdownRenderer>{q.question_text}</MarkdownRenderer>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
                  {q.options.map((opt) => {
                    const isCorrect = opt.label === q.correct_answer;
                    const isWrongPick = opt.label === q.student_answer && !q.is_correct;
                    let bg = C.inset;
                    let border = C.border;
                    let color = C.text;
                    if (isCorrect) {
                      bg = C.correctBg;
                      border = C.correct;
                      color = C.correct;
                    } else if (isWrongPick) {
                      bg = C.wrongBg;
                      border = C.wrong;
                      color = C.wrong;
                    }
                    return (
                      <div
                        key={opt.label}
                        style={{
                          display: 'flex',
                          gap: 10,
                          padding: '9px 12px',
                          border: `1.5px solid ${border}`,
                          borderRadius: 10,
                          background: bg,
                          color,
                          fontSize: 14,
                        }}
                      >
                        <span style={{ fontWeight: 700, minWidth: 16 }}>{opt.label}.</span>
                        <span style={{ flex: 1 }}>
                          <MarkdownRenderer>{opt.text}</MarkdownRenderer>
                        </span>
                        {isCorrect && <span>✓</span>}
                        {isWrongPick && <span>✕</span>}
                      </div>
                    );
                  })}
                </div>
                {q.explanation && (
                  <div style={{ marginTop: 10, fontSize: 14, color: C.text }}>
                    <MarkdownRenderer>{q.explanation}</MarkdownRenderer>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </main>
    </div>
  );
};

export default PracticeReview;
